import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUp } from 'lucide-react';
import {
  addDoc,
  collection,
  onSnapshot,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../lib/auth';
import { DEMO_AIRCRAFT, DEMO_BOOKINGS, DEMO_MAINTENANCE, DEMO_SQUAWKS } from '../../lib/demoData';
import type { AircraftDoc, BookingDoc, MaintenanceItemDoc, SquawkDoc } from '../../lib/types';
import { evaluateBooking, type RuleResult } from '../../lib/bookingRules';
import { localDateTime, localDatePart } from '../../lib/format';
import { nextDueByHours, nextDueByDate } from '../../lib/maintenance';
import { StatusBanner } from '../../components/StatusBanner';
import { soloGateApplies, coiDueInfo, SHOW_SOLO_NOTICE_TO_PILOTS } from '../../lib/soloGate';
import { MonthCalendar, type MaintBlock } from '../../components/MonthCalendar';
import { BOOKING_WINDOW_DAYS } from '../../lib/bookingRules';
import { Wrench } from 'lucide-react';
import { DateTimePicker } from '../../components/DateTimePicker';

import { TRACKED_REG } from '../../lib/flightTrack';
export function BookFlight() {
  const { profile, demoMode, fbUser, isAdmin } = useAuth();
  const coi = coiDueInfo(profile?.leaseEffectiveDate);
  // §7.3 booking window, applied to the picker itself so the days past it simply
  // aren't offered — no error to read, nothing to explain.
  const bookingWindowMax = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + BOOKING_WINDOW_DAYS);
    const p2 = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}T23:59`;
  }, []);
  const todayKey = useMemo(() => localDatePart(new Date()), []);
  const nav = useNavigate();
  const [aircraft, setAircraft] = useState<AircraftDoc[]>([]);
  const [bookings, setBookings] = useState<BookingDoc[]>([]);
  const [squawks, setSquawks] = useState<SquawkDoc[]>([]);
  const [maintenance, setMaintenance] = useState<MaintenanceItemDoc[]>([]);

  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [destinations, setDestinations] = useState('');
  const [notes, setNotes] = useState('');
  // Owner-only recurring booking: repeat the same time block on chosen weekdays for
  // a fixed number of occurrences (never indefinite). Default to Fridays.
  const [repeatOn, setRepeatOn] = useState(false);
  // Owner-only: book the slot as a red maintenance hold instead of a flight.
  const [maintOn, setMaintOn] = useState(false);
  const [maintLabel, setMaintLabel] = useState('Maintenance');
  const [maintCustom, setMaintCustom] = useState('');
  const [repeatDays, setRepeatDays] = useState<Set<number>>(new Set([5]));
  const [repeatCount, setRepeatCount] = useState(4);
  // Owner-only: reserve the slot under a guest pilot's name (someone without a
  // portal profile yet). The booking still lives under the owner's uid.
  const [guestName, setGuestName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resultMsg, setResultMsg] = useState<{ tone: 'success' | 'warning' | 'danger'; title: string; body: string } | null>(null);

  const calRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const resultRef = useRef<HTMLDivElement>(null);

  // Bring the confirmation into view once a booking goes through.
  useEffect(() => {
    if (resultMsg && resultMsg.tone !== 'danger') {
      requestAnimationFrame(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }));
    }
  }, [resultMsg]);

  useEffect(() => {
    if (demoMode) {
      setAircraft(DEMO_AIRCRAFT);
      setBookings(DEMO_BOOKINGS);
      setSquawks(DEMO_SQUAWKS);
      setMaintenance(DEMO_MAINTENANCE);
      return;
    }
    if (!db) return;
    const unsubA = onSnapshot(collection(db, 'aircraft'), (snap) =>
      setAircraft(snap.docs.map((d) => d.data() as AircraftDoc))
    );
    const unsubB = onSnapshot(collection(db, 'bookings'), (snap) =>
      setBookings(snap.docs.map((d) => d.data() as BookingDoc))
    );
    const unsubS = onSnapshot(collection(db, 'squawks'), (snap) =>
      setSquawks(snap.docs.map((d) => d.data() as SquawkDoc))
    );
    const unsubM = onSnapshot(collection(db, 'maintenance'), (snap) =>
      setMaintenance(snap.docs.map((d) => ({ ...(d.data() as MaintenanceItemDoc), itemId: d.id })))
    );
    return () => {
      unsubA();
      unsubB();
      unsubS();
      unsubM();
    };
  }, [demoMode]);

  const ac = aircraft[0];

  // The plane is grounded if an open grounding squawk is on it.
  const grounded =
    Boolean(ac?.groundedByOwner) ||
    squawks.some((s) => s.aircraftId === ac?.aircraftId && s.isGrounding && s.status !== 'resolved');

  // Gray "Maintenance" blocks on the calendar: active groundings (down now,
  // forward) + upcoming airworthiness due-dates as heads-up markers.
  const maintBlocks = useMemo<MaintBlock[]>(() => {
    const blocks: MaintBlock[] = [];
    squawks
      .filter((s) => s.aircraftId === ac?.aircraftId && s.isGrounding && s.status !== 'resolved')
      .forEach((s) => {
        const start = s.dateDiscovered ? new Date(`${s.dateDiscovered}T00:00:00`) : new Date();
        const end = new Date(start.getTime() + 90 * 86400000); // until resolved (capped)
        blocks.push({ start, end, label: ac?.groundedReason || s.description || 'Grounded' });
      });
    if (ac?.maintWindowStart && ac?.maintWindowEnd) {
      blocks.push({
        start: new Date(`${ac.maintWindowStart}T00:00:00`),
        end: new Date(`${ac.maintWindowEnd}T00:00:00`),
        label: ac.maintWindowLabel || 'Scheduled maintenance',
        tone: 'red',
      });
    }
    // One chip per DAY, not per item. The ELT inspection, the annual and anything
    // else riding along all fall on the same date and were stacking up as separate
    // marks on the calendar — but they are one visit to the shop, and the annual is
    // the thing that actually takes the airplane away. Items already inside a
    // scheduled maintenance window are dropped entirely; the window says it better.
    const windowStart = ac?.maintWindowStart ? new Date(`${ac.maintWindowStart}T00:00:00`) : null;
    const windowEnd = ac?.maintWindowEnd ? new Date(`${ac.maintWindowEnd}T00:00:00`) : null;
    const byDay = new Map<string, MaintenanceItemDoc[]>();
    maintenance
      .filter((m) => m.isAirworthiness && m.dueDate)
      .forEach((m) => {
        const due = new Date(`${m.dueDate}T00:00:00`);
        const days = (due.getTime() - Date.now()) / 86400000;
        if (days < -7 || days > 150) return;
        if (windowStart && windowEnd && due >= windowStart && due < windowEnd) return;
        const key = m.dueDate as string;
        const list = byDay.get(key);
        if (list) list.push(m);
        else byDay.set(key, [m]);
      });
    byDay.forEach((items, day) => {
      const due = new Date(`${day}T00:00:00`);
      // The annual grounds the aircraft; everything else that day is done during it.
      const lead = items.find((m) => /annual/i.test(m.label)) ?? items[0];
      const others = items.length - 1;
      blocks.push({
        start: due,
        end: new Date(due.getTime() + 86400000),
        label: others > 0 ? `${lead.label} (+${others} more)` : `${lead.label} (due)`,
      });
    });
    return blocks;
  }, [squawks, maintenance, ac]);

  // Only surface maintenance that is ACTUALLY approaching. This used to print the
  // nearest item on each track no matter how far out it was, so a seat-rail AD 95
  // tach-hours away — the better part of a year of renting — sat next to the
  // booking form looking like a warning. Uses the same due-soon thresholds as the
  // rest of the app (25 hours / 30 days) rather than a second set invented here.
  const maintChip = useMemo(() => {
    if (!ac) return null;
    const HOURS_SOON = 25;
    const DAYS_SOON = 30;
    const short = (s: string) => s.replace(/\s+(change|inspection)$/i, '');
    const parts: string[] = [];
    const byHrs = nextDueByHours(maintenance, ac.currentTach);
    const showHrs = byHrs && byHrs.hoursRemaining <= HOURS_SOON;
    if (showHrs) {
      parts.push(
        byHrs.hoursRemaining < 0
          ? `${short(byHrs.item.label)} ${Math.abs(byHrs.hoursRemaining).toFixed(1)} hr overdue`
          : `${short(byHrs.item.label)} in ${byHrs.hoursRemaining.toFixed(1)} hr`
      );
    }
    const byDate = nextDueByDate(maintenance);
    const showDate = byDate && byDate.daysRemaining <= DAYS_SOON;
    if (showDate) {
      parts.push(
        byDate.daysRemaining < 0
          ? `${short(byDate.item.label)} ${Math.abs(byDate.daysRemaining)}d overdue`
          : `${short(byDate.item.label)} in ${byDate.daysRemaining}d`
      );
    }
    if (!parts.length) return null;
    const urgent =
      (showHrs && byHrs.hoursRemaining <= 10) || (showDate && byDate.daysRemaining <= 14);
    return { text: parts.join(' · '), urgent };
  }, [ac, maintenance]);

  const evaluation = useMemo<RuleResult | null>(() => {
    if (!ac || !profile || !start || !end) return null;
    return evaluateBooking({
      start: new Date(start),
      end: new Date(end),
      aircraft: ac,
      lessee: profile,
      existingBookings: bookings,
      grounded,
    });
  }, [ac, profile, start, end, bookings, grounded]);

  function pickDay(d: Date) {
    // d carries the exact tapped hour from the day timeline; default to a 2.5h block.
    const s = new Date(d);
    const e = new Date(s);
    e.setHours(s.getHours() + 2, 30, 0, 0);
    setStart(localDateTime(s));
    setEnd(localDateTime(e));
    setResultMsg(null);
    requestAnimationFrame(() =>
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    );
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!evaluation || !ac || !profile) return;
    setSubmitting(true);
    setResultMsg(null);

    try {
    const startDt = new Date(start);
    const endDt = new Date(end);

    if (evaluation.outcome === 'reject') {
      setResultMsg({
        tone: 'danger',
        title: 'Cannot book',
        body: evaluation.reasons.join(' '),
      });
      setSubmitting(false);
      return;
    }

    const bookerName =
      (isAdmin && guestName.trim()) ||
      [profile.personal?.firstName, profile.personal?.lastName].filter(Boolean).join(' ') ||
      (isAdmin ? 'AJM (Owner)' : '');
    const baseFields = {
      aircraftId: ac.aircraftId,
      lesseeUid: profile.uid,
      ...(bookerName ? { lesseeName: bookerName } : {}),
      ...(profile.personal?.phone ? { lesseePhone: profile.personal.phone } : {}),
      status: 'auto_confirmed' as const, // no owner approval — active pilots book freely
      expectedDestinations: destinations,
      notes,
      ...(isAdmin && maintOn
        ? { maintenanceLabel: (maintLabel === 'Custom…' ? maintCustom : maintLabel).trim() || 'Maintenance' }
        : {}),
    };
    const overlaps = (s: Date, e: Date) =>
      bookings.some(
        (b) =>
          b.status !== 'cancelled' &&
          b.status !== 'denied' &&
          b.aircraftId === ac.aircraftId &&
          s.getTime() < b.endDateTime.toDate().getTime() &&
          e.getTime() > b.startDateTime.toDate().getTime()
      );

    // Owner recurring: clone the time block onto the next N chosen weekdays, skipping
    // any that conflict. Bounded by repeatCount — never indefinite.
    if (isAdmin && repeatOn && repeatDays.size > 0) {
      const durationMs = endDt.getTime() - startDt.getTime();
      const occ: { s: Date; e: Date }[] = [];
      const cur = new Date(startDt);
      let guard = 0;
      while (occ.length < repeatCount && guard < 400) {
        if (repeatDays.has(cur.getDay()) && cur.getTime() >= startDt.getTime() - 60000) {
          const s = new Date(cur);
          s.setHours(startDt.getHours(), startDt.getMinutes(), 0, 0);
          occ.push({ s, e: new Date(s.getTime() + durationMs) });
        }
        cur.setDate(cur.getDate() + 1);
        guard++;
      }
      let created = 0;
      let skipped = 0;
      for (const o of occ) {
        if (overlaps(o.s, o.e)) {
          skipped++;
          continue;
        }
        if (demoMode) {
          setBookings((curr) => [
            ...curr,
            { ...baseFields, bookingId: `b-${o.s.getTime()}`, startDateTime: Timestamp.fromDate(o.s), endDateTime: Timestamp.fromDate(o.e), createdAt: Timestamp.now() } as BookingDoc,
          ]);
        } else if (db && fbUser) {
          await addDoc(collection(db, 'bookings'), {
            ...baseFields,
            startDateTime: Timestamp.fromDate(o.s),
            endDateTime: Timestamp.fromDate(o.e),
            createdAt: serverTimestamp(),
          });
        }
        created++;
      }
      setResultMsg({
        tone: created > 0 ? 'success' : 'warning',
        title: created > 0 ? `${created} booking${created !== 1 ? 's' : ''} added` : 'No bookings added',
        body: `${created} recurring slot${created !== 1 ? 's' : ''} are on the calendar${skipped ? `, ${skipped} skipped for conflicts` : ''}.`,
      });
      setStart('');
      setEnd('');
      setDestinations('');
      setNotes('');
      setGuestName('');
      setSubmitting(false);
      return;
    }

    const newBooking: BookingDoc = {
      bookingId: `b-${Date.now()}`,
      ...baseFields,
      startDateTime: Timestamp.fromDate(startDt),
      endDateTime: Timestamp.fromDate(endDt),
      createdAt: Timestamp.now(),
    };

    if (demoMode) {
      setBookings((curr) => [...curr, newBooking]);
    } else if (db && fbUser) {
      await addDoc(collection(db, 'bookings'), {
        ...newBooking,
        startDateTime: Timestamp.fromDate(startDt),
        endDateTime: Timestamp.fromDate(endDt),
        createdAt: serverTimestamp(),
      });
    }

    setResultMsg({
      tone: 'success',
      title: 'Booking confirmed',
      body: 'You’re on the schedule. It now shows on the calendar for everyone.',
    });
    setStart('');
    setEnd('');
    setDestinations('');
    setNotes('');
    setGuestName('');
    setSubmitting(false);
    } catch (err) {
      setResultMsg({
        tone: 'danger',
        title: 'Booking failed',
        body: err instanceof Error ? err.message : 'Could not save the booking. Please try again.',
      });
      setSubmitting(false);
    }
  }

  // Owner is exempt from the lessee gates — no lease to sign, no card to decline.
  // These must mirror evaluateBooking() in lib/bookingRules.ts; when they drifted,
  // the owner was shown a renter's blocker on his own airplane.
  const blocked = isAdmin
    ? null
    : profile?.status !== 'active'
      ? `Your account status is ${profile?.status}. Booking is available once you’re active.`
      : !profile?.leaseSignedAt
      ? 'Your lease hasn’t been signed yet. Booking opens as soon as the signed lease is on file — check your email for the agreement.'
      : profile?.paymentPastDue || profile?.paymentFailedFlag
      ? 'A payment on your account didn’t go through. Update your card on the Billing page to resume booking.'
      : null;

  return (
    <>
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-[26px] sm:text-[34px] font-bold leading-tight tracking-tight text-navy-900">
            Book a flight
          </h1>
          <p className="mt-1 text-sm text-ink-muted">
            {ac ? `${ac.tailNumber} · $${ac.hourlyRate}/Hobbs hr dry` : 'Reserve the aircraft.'}
          </p>
          {maintChip && (
            <span
              className={`mt-2 inline-flex max-w-full items-center gap-1.5 whitespace-nowrap overflow-hidden rounded-full px-2.5 py-1 text-[11px] font-medium ${
                maintChip.urgent
                  ? 'bg-amber-100 text-amber-800'
                  : 'bg-slate-100 text-slate-600'
              }`}
            >
              <Wrench size={12} className="shrink-0" /> <span className="truncate">{maintChip.text}</span>
            </span>
          )}
        </div>
        <button className="btn-secondary shrink-0 text-sm" onClick={() => nav(isAdmin ? '/admin/flights' : '/flights')}>
          My flights
        </button>
      </div>

      {grounded && (
        <StatusBanner tone="danger" title="Aircraft grounded">
          {TRACKED_REG} has an open grounding squawk and can’t be booked until the owner resolves it.
        </StatusBanner>
      )}

      {blocked && <StatusBanner tone="warning" title="Booking disabled">{blocked}</StatusBanner>}

      {/* Not a block — booking dual instruction is exactly what §11.2 permits here. */}
      {SHOW_SOLO_NOTICE_TO_PILOTS && soloGateApplies(profile) && (
        <StatusBanner tone="warning" title="Dual instruction only — no solo pilot in command">
          AJM doesn&rsquo;t have your certificate of insurance yet. Under Section 11.2 you may fly {TRACKED_REG} only
          with a flight instructor named and approved on AJM&rsquo;s policy until it&rsquo;s on file.
          {coi && (coi.overdue ? ` It was due ${coi.dueDate}.` : ` It&rsquo;s due by ${coi.dueDate}.`)} Book away
          — just make sure a named CFI is with you.
        </StatusBanner>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-4">
        <div ref={calRef} className="lg:col-span-2 scroll-mt-20">
          <MonthCalendar bookings={bookings} myUid={profile?.uid} onPickDay={pickDay} maintBlocks={maintBlocks} />
        </div>

        <div className="space-y-4 self-start">
          <form ref={formRef} onSubmit={submit} className="card p-5 space-y-4 scroll-mt-20">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-lg font-semibold text-navy-900">New booking</h2>
            <button
              type="button"
              onClick={() => calRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              className="lg:hidden inline-flex items-center gap-1 text-sm text-navy-700"
            >
              <ArrowUp size={14} /> Calendar
            </button>
          </div>
          {start && (
            <p className="-mt-1 text-sm text-ink-muted">
              {new Date(start).toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })}
            </p>
          )}
          <div>
            <label className="label">Start</label>
            <DateTimePicker
              value={start}
              min={isAdmin ? undefined : todayKey}
              max={isAdmin ? undefined : bookingWindowMax}
              onChange={(v) => {
                setStart(v);
                // Keep End after Start: default to a 2.5h block until the pilot changes it.
                if (v && (!end || end <= v)) {
                  const e = new Date(v);
                  e.setMinutes(e.getMinutes() + 150);
                  setEnd(localDateTime(e));
                }
              }}
            />
          </div>
          <div>
            <label className="label">End</label>
            <DateTimePicker
              value={end}
              min={isAdmin ? undefined : start ? start.slice(0, 10) : todayKey}
              onChange={setEnd}
              placeholder="Pick a return time"
              defaultHour={10}
            />
          </div>
          <div>
            <label className="label">Expected destinations (optional)</label>
            <input
              className="input"
              placeholder="KLGB → KCMA → KLGB"
              value={destinations}
              onChange={(e) => setDestinations(e.target.value)}
            />
          </div>
          <div>
            <label className="label">Notes (optional)</label>
            <textarea
              className="input-textarea"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          {isAdmin && (
            <div>
              <label className="label">Booking for (owner only, optional)</label>
              <input
                className="input"
                placeholder="Guest pilot's name — blank books it as you"
                value={guestName}
                onChange={(e) => setGuestName(e.target.value)}
              />
              <p className="mt-1 text-xs text-ink-muted">
                Puts this name on the calendar for a pilot who doesn’t have a portal profile yet.
              </p>
            </div>
          )}

          {isAdmin && (
            <div className="rounded-xl border border-red-200 bg-red-50/60 p-4">
              <label className="flex items-center gap-2 text-sm font-medium text-red-900">
                <input type="checkbox" checked={maintOn} onChange={(e) => setMaintOn(e.target.checked)} />
                Maintenance hold (owner only) — shows red on every calendar
              </label>
              {maintOn && (
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label className="label">Label</label>
                    <select className="input" value={maintLabel} onChange={(e) => setMaintLabel(e.target.value)}>
                      {['Maintenance', 'Annual Inspection', 'Oil Change', 'Squawk Repair', 'Avionics', 'Detail / Wash', 'Custom…'].map((o) => (
                        <option key={o}>{o}</option>
                      ))}
                    </select>
                  </div>
                  {maintLabel === 'Custom…' && (
                    <div>
                      <label className="label">Custom label</label>
                      <input className="input" value={maintCustom} onChange={(e) => setMaintCustom(e.target.value)} placeholder="e.g. Prop balance" />
                    </div>
                  )}
                  <p className="text-xs text-red-800 sm:col-span-2">
                    Books the slot like any booking — nobody else can take it — and renders as a red
                    maintenance block with this label.
                  </p>
                </div>
              )}
            </div>
          )}

          {isAdmin && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <label className="flex items-center gap-2 text-sm font-medium text-navy-900">
                <input type="checkbox" checked={repeatOn} onChange={(e) => setRepeatOn(e.target.checked)} />
                Repeat this weekly (owner only)
              </label>
              {repeatOn && (
                <div className="mt-3 space-y-3">
                  <div>
                    <p className="mb-1.5 text-xs text-ink-muted">On these days</p>
                    <div className="flex gap-1.5">
                      {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((lbl, i) => {
                        const on = repeatDays.has(i);
                        return (
                          <button
                            key={i}
                            type="button"
                            onClick={() =>
                              setRepeatDays((prev) => {
                                const n = new Set(prev);
                                if (n.has(i)) n.delete(i);
                                else n.add(i);
                                return n;
                              })
                            }
                            className={`h-9 w-9 rounded-full text-sm font-medium transition ${
                              on ? 'bg-brand text-white' : 'bg-white text-ink-muted ring-1 ring-slate-200'
                            }`}
                          >
                            {lbl}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <label className="text-sm text-navy-900">How many?</label>
                    <input
                      type="number"
                      min={1}
                      max={52}
                      value={repeatCount}
                      onChange={(e) => setRepeatCount(Math.max(1, Math.min(52, Number(e.target.value) || 1)))}
                      className="input w-20"
                    />
                    <span className="text-xs text-ink-muted">
                      occurrences — the date/time above is the first one
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {evaluation && (
            <div
              className={`rounded-lg border px-3 py-2 text-sm ${
                evaluation.outcome === 'reject'
                  ? 'bg-red-50 border-red-200 text-red-800'
                  : 'bg-emerald-50 border-emerald-200 text-emerald-900'
              }`}
            >
              <div className="font-medium">
                {evaluation.outcome === 'reject' ? 'Can’t book this slot' : 'Ready to book'}
              </div>
              <ul className="list-disc pl-5 mt-1">
                {evaluation.reasons.map((r) => (
                  <li key={r}>{r}</li>
                ))}
              </ul>
            </div>
          )}

          {resultMsg && (
            <div
              ref={resultRef}
              className={`scroll-mt-24 rounded-xl border px-4 py-3 text-sm ${
                resultMsg.tone === 'success'
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                  : resultMsg.tone === 'warning'
                  ? 'bg-amber-50 border-amber-200 text-amber-900'
                  : 'bg-red-50 border-red-200 text-red-800'
              }`}
            >
              <div className="font-semibold">{resultMsg.title}</div>
              <div className="opacity-90">{resultMsg.body}</div>
              {resultMsg.tone !== 'danger' && (
                <div className="mt-3 flex flex-wrap gap-2">
                  <button type="button" onClick={() => nav(isAdmin ? '/admin/bookings' : '/bookings')} className="btn-primary py-1.5 text-sm">
                    View my bookings
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setResultMsg(null);
                      calRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                    className="btn-secondary py-1.5 text-sm"
                  >
                    Book another
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Hide the submit button once a booking is confirmed — the result block drives next steps. */}
          {!(resultMsg && resultMsg.tone !== 'danger') && (
            <button
              type="submit"
              disabled={!evaluation || evaluation.outcome === 'reject' || submitting || Boolean(blocked)}
              className="btn-primary w-full"
            >
              {submitting
                ? 'Submitting…'
                : isAdmin && repeatOn && repeatDays.size > 0
                ? `Add ${repeatCount} booking${repeatCount !== 1 ? 's' : ''}`
                : 'Confirm booking'}
            </button>
          )}
        </form>
        </div>
      </div>
    </>
  );
}
